/** @jsxImportSource react */
import { useMemo, useRef, useState } from 'react'
import {
  Alert, Box, Breadcrumbs, Button, LinearProgress, Link, MenuItem, Paper, Stack, Step, StepLabel, Stepper, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import Papa from 'papaparse'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { recordsAPI } from '../../api/crm'
import { useEntityTypes, usePublishedForm, useTenantKey } from '../../hooks/useCrmQueries'
import { allFields } from '../../components/records/fieldValues'
import { downloadBlob, entityLabel, entityLabelPlural } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'
import Loading from '../../components/common/Loading'

const MAX_ROWS = 10_000
const BATCH = 500
const SKIP = ''
const STEPS = ['Upload', 'Map columns', 'Check', 'Import']

/** "Full name", "full_name", "FullName" → "fullname" (for auto-matching columns to fields) */
const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '')

const pad = (n) => String(n).padStart(2, '0')

/** An Excel cell as the text the import expects: dates ISO (yyyy-mm-dd, with THH:mm when there's a time), Yes / No. */
const excelText = (v) => {
  if (v == null) return ''
  if (v instanceof Date) {
    // the reader gives a sheet's date-time as that wall-clock time in UTC
    const date = `${v.getUTCFullYear()}-${pad(v.getUTCMonth() + 1)}-${pad(v.getUTCDate())}`
    return v.getUTCHours() || v.getUTCMinutes() ? `${date}T${pad(v.getUTCHours())}:${pad(v.getUTCMinutes())}` : date
  }
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  return String(v)
}

/** An ISO date into a date-time field gets 00:00; a date-time into a date field keeps the date. */
const fitDate = (field, value) => {
  if (field?.fieldType === 'DATE' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return value.slice(0, 10)
  if (field?.fieldType === 'DATETIME' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T00:00`
  return value
}

const RecordImportPage = () => {
  const { entityType } = useParams()
  const { forms, isLoading } = useEntityTypes()
  const entityForms = forms.filter((f) => f.entityType === entityType)
  const [formCode, setFormCode] = useState(null)
  const code = formCode || entityForms[0]?.code
  const { data: form } = usePublishedForm(code)

  if (isLoading || (code && !form)) return <Loading />
  if (!code) return <Alert severity="info">Publish a form for {entityLabelPlural(entityType)} before importing.</Alert>
  return <Importer key={code} entityType={entityType} form={form} entityForms={entityForms} onFormChange={setFormCode} />
}

const Importer = ({ entityType, form, entityForms, onFormChange }) => {
  const navigate = useNavigate()
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const fileInput = useRef(null)
  const fields = useMemo(() => allFields(form), [form])

  const [step, setStep] = useState(0)
  const [file, setFile] = useState(null) // { name, columns, rows }
  const [parseError, setParseError] = useState(null)
  const [mapping, setMapping] = useState({}) // file column → fieldKey
  const [progress, setProgress] = useState(null) // { done, total }
  const [check, setCheck] = useState(null) // { valid, errors: [{ row, errors }] }
  const [result, setResult] = useState(null) // { created, errors }
  const [runError, setRunError] = useState(null)
  const [mode, setMode] = useState('CREATE') // CREATE | UPDATE | SKIP: rows matching an existing record
  const [matchField, setMatchField] = useState('')

  // header row + data rows (objects keyed by column name), from either CSV or an Excel sheet
  const loaded = (name, columns, data) => {
    if (data.length > MAX_ROWS) return setParseError(`The file has ${data.length} rows; import at most ${MAX_ROWS} at a time.`)
    if (!data.length) return setParseError('The file has no data rows.')
    // auto-map by label or key; first match wins, so two columns never map to one field
    const used = new Set()
    const auto = {}
    columns.forEach((col) => {
      const match = fields.find((f) => !used.has(f.fieldKey) && (norm(f.label) === norm(col) || norm(f.fieldKey) === norm(col)))
      auto[col] = match ? match.fieldKey : SKIP
      if (match) used.add(match.fieldKey)
    })
    setFile({ name, columns, rows: data })
    setMapping(auto)
    setStep(1)
  }

  const parse = (selected) => {
    setParseError(null)
    if (/\.xlsx$/i.test(selected.name)) return parseExcel(selected)
    if (/\.xls$/i.test(selected.name)) return setParseError('Old .xls files can’t be read; save it as .xlsx or CSV in Excel.')
    Papa.parse(selected, {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: (h) => h.replace(/^\uFEFF/, '').trim(),
      complete: ({ data, meta, errors }) => {
        if (errors.length && !data.length) return setParseError(errors[0].message)
        loaded(selected.name, meta.fields.filter(Boolean), data)
      },
      error: (e) => setParseError(e.message),
    })
  }

  // the first sheet of a workbook; loaded on demand so the reader isn't in the main bundle
  const parseExcel = async (selected) => {
    try {
      const { readSheet } = await import('read-excel-file/browser')
      const sheet = await readSheet(selected)
      const [header = [], ...body] = sheet
      const columns = header.map((h) => excelText(h).trim())
      const keep = columns.map((c, i) => [c, i]).filter(([c]) => c)
      const data = body
        .map((cells) => Object.fromEntries(keep.map(([c, i]) => [c, excelText(cells[i])])))
        .filter((row) => Object.values(row).some((v) => v.trim()))
      loaded(selected.name, keep.map(([c]) => c), data)
    } catch (e) {
      setParseError(`Couldn’t read this Excel file (${e.message}). Save it as CSV and try again.`)
    }
  }

  const mappedFields = Object.values(mapping).filter(Boolean)
  const duplicateTargets = mappedFields.filter((k, i) => mappedFields.indexOf(k) !== i)
  // rows can be matched to existing records on a mapped email, phone or text field
  const matchKeys = fields.filter((f) => ['EMAIL', 'PHONE', 'TEXT'].includes(f.fieldType) && mappedFields.includes(f.fieldKey))
  const missingRequired = fields.filter((f) => f.required && !f.readOnly && !f.defaultValue && !mappedFields.includes(f.fieldKey))

  /** Rows as { fieldKey: text } for the mapped columns. */
  const byKey = Object.fromEntries(fields.map((f) => [f.fieldKey, f]))
  const mappedRows = () => file.rows.map((row) => {
    const out = {}
    Object.entries(mapping).forEach(([col, key]) => { if (key) out[key] = fitDate(byKey[key], row[col] ?? '') })
    return out
  })

  // sends all rows in batches; rows are numbered as in the file (header = row 1)
  const runBatches = async (dryRun) => {
    const rows = mappedRows()
    let valid = 0
    let created = 0
    let updated = 0
    let toCreate = 0
    let toUpdate = 0
    let skipped = 0
    const errors = []
    setProgress({ done: 0, total: rows.length })
    for (let start = 0; start < rows.length; start += BATCH) {
      const response = await recordsAPI.importRows({
        formCode: form.code, rows: rows.slice(start, start + BATCH), dryRun, startRow: start + 2,
        mode, matchField: mode === 'CREATE' ? undefined : matchField,
      })
      valid += response.valid
      created += response.created
      updated += response.updated
      toCreate += response.toCreate
      toUpdate += response.toUpdate
      skipped += response.skipped
      errors.push(...response.errors)
      setProgress({ done: Math.min(start + BATCH, rows.length), total: rows.length })
    }
    setProgress(null)
    return { valid, created, updated, toCreate, toUpdate, skipped, errors }
  }

  const runCheck = async () => {
    setRunError(null)
    setStep(2)
    try {
      setCheck(await runBatches(true))
    } catch (e) {
      setProgress(null)
      setRunError(getErrorMessage(e))
    }
  }

  const runImport = async () => {
    setRunError(null)
    setStep(3)
    try {
      const done = await runBatches(false)
      setResult(done)
      queryClient.invalidateQueries({ queryKey: [tenantId, 'records'] })
    } catch (e) {
      setProgress(null)
      setRunError(getErrorMessage(e))
    }
  }

  /** The failed rows with an extra "Errors" column, to fix and re-import. */
  const downloadErrors = (errors) => {
    const byRow = new Map(errors.map((e) => [e.row, withLabels(e.errors, fields).join('; ')]))
    const failed = file.rows.flatMap((row, i) => (byRow.has(i + 2) ? [{ ...row, Errors: byRow.get(i + 2) }] : []))
    const csv = Papa.unparse(failed, { columns: [...file.columns, 'Errors'] })
    downloadBlob(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' }), `${entityType.toLowerCase()}-import-errors.csv`)
  }

  const label = entityLabelPlural(entityType).toLowerCase()

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to={`/records/${entityType}`} underline="hover" color="inherit">{entityLabelPlural(entityType)}</Link>
        <Typography color="text.primary">Import</Typography>
      </Breadcrumbs>
      <Typography variant="h4" sx={{ mb: 2 }}>Import {label}</Typography>
      <Stepper activeStep={step} sx={{ mb: 3, maxWidth: 720 }}>
        {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
      </Stepper>

      {runError && <Alert severity="error" sx={{ mb: 2 }}>{runError}</Alert>}
      {progress && (
        <Box sx={{ mb: 2, maxWidth: 520 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{progress.done} of {progress.total} rows…</Typography>
          <LinearProgress variant="determinate" value={(progress.done / progress.total) * 100} />
        </Box>
      )}

      {step === 0 && (
        <Paper variant="outlined" sx={{ p: 3, maxWidth: 640 }}>
          {entityForms.length > 1 && (
            <TextField select label="Form" value={form.code} onChange={(e) => onFormChange(e.target.value)} sx={{ mb: 2 }}>
              {entityForms.map((f) => <MenuItem key={f.code} value={f.code}>{f.name}</MenuItem>)}
            </TextField>
          )}
          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) parse(e.dataTransfer.files[0]) }}
            onClick={() => fileInput.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInput.current?.click() }}
            sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 2, p: 5, textAlign: 'center', cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
          >
            <UploadFileOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
            <Typography sx={{ mt: 1 }}>Drop an Excel (.xlsx) or CSV file here, or click to choose</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              First row = column names; from Excel, the first sheet is read. Up to {MAX_ROWS.toLocaleString()} rows. Tip: export the list first to get a template.
            </Typography>
            <input ref={fileInput} type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={(e) => { if (e.target.files[0]) parse(e.target.files[0]); e.target.value = '' }} />
          </Box>
          {parseError && <Alert severity="error" sx={{ mt: 2 }}>{parseError}</Alert>}
        </Paper>
      )}

      {step === 1 && file && (
        <Stack spacing={2} sx={{ maxWidth: 900 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{file.name}</strong> · {file.rows.length} rows. Choose which field each column goes into.
            Dropdowns accept option names; checkboxes Yes/No; lookups the linked record’s name.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Column in file</TableCell>
                  <TableCell>Example</TableCell>
                  <TableCell sx={{ width: 280 }}>Import into</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {file.columns.map((col) => (
                  <TableRow key={col}>
                    <TableCell sx={{ fontWeight: 500 }}>{col}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {file.rows.find((r) => r[col])?.[col] ?? ''}
                    </TableCell>
                    <TableCell>
                      <TextField select value={mapping[col] ?? SKIP} onChange={(e) => setMapping((m) => ({ ...m, [col]: e.target.value }))}
                        SelectProps={{ displayEmpty: true }}
                        error={!!mapping[col] && duplicateTargets.includes(mapping[col])} inputProps={{ 'aria-label': `Field for ${col}` }}>
                        <MenuItem value={SKIP}><em>Don’t import</em></MenuItem>
                        {fields.filter((f) => !f.readOnly).map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}{f.required ? ' *' : ''}</MenuItem>)}
                      </TextField>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {duplicateTargets.length > 0 && <Alert severity="error">Two columns go into the same field. Each field can take one column.</Alert>}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>When a row matches a record you already have</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Then" value={mode} onChange={(e) => setMode(e.target.value)} sx={{ minWidth: 260 }}>
                <MenuItem value="CREATE">Create a new record anyway</MenuItem>
                <MenuItem value="UPDATE">Update the existing record</MenuItem>
                <MenuItem value="SKIP">Skip the row</MenuItem>
              </TextField>
              {mode !== 'CREATE' && (
                <TextField select label="Match on" value={matchField} onChange={(e) => setMatchField(e.target.value)} sx={{ minWidth: 220 }}
                  error={!matchField} helperText={matchKeys.length ? 'Case and spacing are ignored; phones by their last 10 digits' : 'Map an email, phone or text column first'}>
                  {matchKeys.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
                </TextField>
              )}
            </Stack>
            {mode === 'UPDATE' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Filled cells replace the record’s values; empty cells leave them as they are.
              </Typography>
            )}
          </Paper>
          {missingRequired.length > 0 && (
            <Alert severity="warning">Required field{missingRequired.length > 1 ? 's' : ''} not mapped: {missingRequired.map((f) => f.label).join(', ')}. Those rows will fail.</Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => { setFile(null); setStep(0) }}>Back</Button>
            <Button variant="contained" onClick={runCheck}
              disabled={mappedFields.length === 0 || duplicateTargets.length > 0 || (mode !== 'CREATE' && !matchKeys.some((f) => f.fieldKey === matchField))}>
              Check {file.rows.length} rows
            </Button>
          </Box>
        </Stack>
      )}

      {step === 2 && check && (
        <Stack spacing={2} sx={{ maxWidth: 900 }}>
          <Alert severity={check.errors.length ? 'warning' : 'success'}>
            {check.valid} of {file.rows.length} rows are ready to import
            {mode !== 'CREATE' && ` (${check.toCreate} new, ${check.toUpdate} update${check.toUpdate === 1 ? 's' : ''} of existing records${check.skipped ? `, ${check.skipped} already exist and will be skipped` : ''})`}.
            {check.errors.length > 0 && ` ${check.errors.length} have problems and will be skipped.`}
          </Alert>
          {check.errors.length > 0 && <ErrorTable errors={check.errors} fields={fields} onDownload={() => downloadErrors(check.errors)} />}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => { setCheck(null); setStep(1) }}>Back to mapping</Button>
            <Button variant="contained" onClick={runImport} disabled={check.valid === 0}>
              Import {check.valid} {check.valid === 1 ? entityLabel(entityType).toLowerCase() : label}
            </Button>
          </Box>
        </Stack>
      )}

      {step === 3 && result && (
        <Stack spacing={2} sx={{ maxWidth: 900 }}>
          <Alert severity="success">
            Imported {result.created} new {result.created === 1 ? entityLabel(entityType).toLowerCase() : label}
            {result.updated > 0 && `, updated ${result.updated}`}{result.skipped > 0 && `, skipped ${result.skipped} that already existed`}.
          </Alert>
          {result.errors.length > 0 && <ErrorTable errors={result.errors} fields={fields} onDownload={() => downloadErrors(result.errors)} />}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={() => navigate(`/records/${entityType}`)}>View {label}</Button>
            <Button onClick={() => { setFile(null); setCheck(null); setResult(null); setStep(0) }}>Import another file</Button>
          </Box>
        </Stack>
      )}
    </Box>
  )
}

/** "fullName: is required" → "Full name: is required" */
const withLabels = (messages, fields) => messages.map((m) => {
  const i = m.indexOf(': ')
  const field = i > 0 && fields.find((f) => f.fieldKey === m.slice(0, i))
  return field ? `${field.label}: ${m.slice(i + 2)}` : m
})

const ErrorTable = ({ errors, fields, onDownload }) => (
  <Paper variant="outlined">
    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
      <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>Rows with problems</Typography>
      <Button onClick={onDownload}>Download these rows</Button>
    </Box>
    <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 80 }}>Row</TableCell>
            <TableCell>Problem</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {errors.slice(0, 200).map((e) => (
            <TableRow key={e.row}>
              <TableCell>{e.row}</TableCell>
              <TableCell>{withLabels(e.errors, fields).join(' · ')}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
    {errors.length > 200 && <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>Showing 200 of {errors.length}; download for all.</Typography>}
  </Paper>
)

export default RecordImportPage
